import type { ReactNode } from 'react'
import type { Tone } from '@/constants/clinic'
import type { Appointment } from '@/types/clinic'

import { useEffect, useMemo, useState } from 'react'
import { cn } from '@/lib/utils'
import { useClinic } from '@/lib/clinic/store'
import { useLookups } from '@/hooks/use-clinic-helpers'
import AppointmentPopover from '@/components/appointments/appointment-popover'

import {
    dayOfWeek,
    formatTime,
    formatWeekday,
    minutesToTime,
    parseISODate,
    timeToMinutes
} from '@/lib/dates'

import { APPOINTMENT_STATUS_META } from '@/constants/clinic'

const HOUR_HEIGHT = 64

export const BLOCK_TONES: Record<Tone, string> = {
    slate: 'border-slate-400 bg-slate-50 text-slate-800 hover:bg-slate-100',
    teal: 'border-teal-500 bg-teal-50 text-teal-900 hover:bg-teal-100',
    blue: 'border-sky-500 bg-sky-50 text-sky-900 hover:bg-sky-100',
    green: 'border-emerald-500 bg-emerald-50 text-emerald-900 hover:bg-emerald-100',
    amber: 'border-amber-500 bg-amber-50 text-amber-900 hover:bg-amber-100',
    red: 'border-rose-400 bg-rose-50/80 text-rose-800 line-through decoration-rose-300 hover:bg-rose-100',
    orange: 'border-orange-400 bg-orange-50 text-orange-900 hover:bg-orange-100',
    violet: 'border-violet-500 bg-violet-50 text-violet-900 hover:bg-violet-100'
}

interface Placed {
    appointment: Appointment
    start: number
    end: number
    lane: number
    lanes: number
}

// Places overlapping appointments side by side, like a desk calendar.
const layoutDay = (items: Appointment[]): Placed[] => {
    const sorted = [...items].sort((a, b) => timeToMinutes(a.time) - timeToMinutes(b.time) || b.duration - a.duration)
    const result: Placed[] = []
    let cluster: Placed[] = []
    let laneEnds: number[] = []
    let clusterEnd = -1

    const flush = () => {
        const lanes = Math.max(laneEnds.length, 1)
        cluster.forEach(item => {
            item.lanes = lanes
        })
        result.push(...cluster)
        cluster = []
        laneEnds = []
    }

    for (const appointment of sorted) {
        const start = timeToMinutes(appointment.time)
        const end = start + Math.max(appointment.duration, 15)
        if (cluster.length && start >= clusterEnd) flush()
        let lane = laneEnds.findIndex(laneEnd => laneEnd <= start)
        if (lane === -1) {
            lane = laneEnds.length
            laneEnds.push(end)
        }
        else {
            laneEnds[lane] = end
        }
        cluster.push({ appointment, start, end, lane, lanes: 1 })
        clusterEnd = Math.max(clusterEnd, end)
    }
    flush()
    return result
}

interface CalendarTimeGridProps {
    days: string[]
    appointments: Appointment[]
    onSlotClick: (date: string, time: string) => void
}

const CalendarTimeGrid = ({ days, appointments, onSlotClick }: CalendarTimeGridProps): ReactNode => {
    const { data, today } = useClinic()
    const { patientsById } = useLookups()
    const settings = data.settings.appointments
    const [nowMinutes, setNowMinutes] = useState<number | null>(null)

    useEffect(() => {
        const tick = () => {
            const now = new Date()
            setNowMinutes(now.getHours() * 60 + now.getMinutes())
        }
        tick()
        const id = window.setInterval(tick, 60000)
        return () => window.clearInterval(id)
    }, [])

    const visible = useMemo(() => appointments.filter(item => days.includes(item.date)), [appointments, days])

    const bounds = useMemo(() => {
        const starts = visible.map(item => timeToMinutes(item.time))
        const ends = visible.map(item => timeToMinutes(item.time) + item.duration)
        const start = Math.floor(Math.min(timeToMinutes(settings.openTime), ...starts) / 60) * 60
        const end = Math.ceil(Math.max(timeToMinutes(settings.closeTime), ...ends) / 60) * 60
        return { start, end: Math.max(end, start + 60) }
    }, [visible, settings.openTime, settings.closeTime])

    const hours = Array.from({ length: (bounds.end - bounds.start) / 60 }, (_, index) => bounds.start + index * 60)
    const height = ((bounds.end - bounds.start) / 60) * HOUR_HEIGHT
    const top = (minutes: number) => ((minutes - bounds.start) / 60) * HOUR_HEIGHT
    const slots = Array.from({ length: (bounds.end - bounds.start) / 30 }, (_, index) => bounds.start + index * 30)
    const columns = `4rem repeat(${days.length}, minmax(0, 1fr))`

    return (
        <div className='overflow-x-auto'>
            <div className={cn(days.length > 1 && 'min-w-[760px]')}>
                <div className='sticky top-0 z-10 grid border-b bg-card' style={{ gridTemplateColumns: columns }}>
                    <div />
                    {days.map(day => {
                        const closed = !settings.workingDays.includes(dayOfWeek(day))
                        const isToday = day === today
                        const count = visible.filter(item => item.date === day && item.status !== 'cancelled').length
                        return (
                            <div key={day} className='border-l px-2 py-2.5 text-center'>
                                <p className={cn('text-xs font-medium uppercase', isToday ? 'text-primary' : 'text-muted-foreground')}>{formatWeekday(day)}</p>
                                <p className={cn(
                                    'mx-auto mt-0.5 flex size-8 items-center justify-center rounded-full text-sm font-semibold',
                                    isToday ? 'bg-primary text-primary-foreground' : 'text-foreground'
                                )}
                                >
                                    {parseISODate(day).getDate()}
                                </p>
                                <p className='mt-0.5 text-[11px] text-muted-foreground'>
                                    {closed ? 'Closed' : count ? `${count} booked` : 'Free'}
                                </p>
                            </div>
                        )
                    })}
                </div>

                <div className='relative grid' style={{ gridTemplateColumns: columns, height }}>
                    <div className='relative'>
                        {hours.map(minutes => (
                            <span
                                key={minutes}
                                className='absolute right-2 -translate-y-1/2 text-[11px] text-muted-foreground tabular-nums'
                                style={{ top: top(minutes) }}
                            >
                                {minutes === bounds.start ? '' : formatTime(minutesToTime(minutes)).replace(':00', '')}
                            </span>
                        ))}
                    </div>

                    {days.map(day => {
                        const closed = !settings.workingDays.includes(dayOfWeek(day))
                        const placed = layoutDay(visible.filter(item => item.date === day))
                        return (
                            <div key={day} className={cn('relative border-l', closed && 'bg-muted/50')}>
                                {hours.map(minutes => (
                                    <div key={minutes} className='pointer-events-none absolute inset-x-0 border-t border-border/80' style={{ top: top(minutes) }} />
                                ))}
                                {hours.map(minutes => (
                                    <div key={`half-${minutes}`} className='pointer-events-none absolute inset-x-0 border-t border-dashed border-border/50' style={{ top: top(minutes + 30) }} />
                                ))}

                                {!closed && settings.breakStart && settings.breakEnd && (
                                    <div
                                        className='pointer-events-none absolute inset-x-0 flex items-start justify-center bg-muted/60 pt-1 text-[10px] font-medium tracking-wide text-muted-foreground uppercase'
                                        style={{ top: top(timeToMinutes(settings.breakStart)), height: top(timeToMinutes(settings.breakEnd)) - top(timeToMinutes(settings.breakStart)) }}
                                    >
                                        Break
                                    </div>
                                )}

                                {slots.map(minutes => (
                                    <button
                                        key={`slot-${minutes}`}
                                        type='button'
                                        tabIndex={-1}
                                        aria-hidden='true'
                                        onClick={() => onSlotClick(day, minutesToTime(minutes))}
                                        className='absolute inset-x-0 transition-colors hover:bg-primary/5'
                                        style={{ top: top(minutes), height: HOUR_HEIGHT / 2 }}
                                    />
                                ))}

                                {day === today && nowMinutes !== null && nowMinutes >= bounds.start && nowMinutes <= bounds.end && (
                                    <div className='pointer-events-none absolute inset-x-0 z-[5] flex items-center' style={{ top: top(nowMinutes) }}>
                                        <span className='-ml-1 size-2 rounded-full bg-rose-500' />
                                        <span className='h-px flex-1 bg-rose-500' />
                                    </div>
                                )}

                                {placed.map(({ appointment, start, end, lane, lanes }) => {
                                    const patient = patientsById.get(appointment.patientId)
                                    const blockHeight = Math.max(top(end) - top(start) - 2, 22)
                                    const tone = APPOINTMENT_STATUS_META[appointment.status].tone
                                    return (
                                        <AppointmentPopover
                                            key={appointment.id}
                                            appointment={appointment}
                                            trigger={(
                                                <button
                                                    type='button'
                                                    className={cn(
                                                        'absolute z-[6] overflow-hidden rounded-md border-l-[3px] px-1.5 py-1 text-left text-xs shadow-card transition-colors focus-visible:ring-3 focus-visible:ring-ring/50 focus-visible:outline-none',
                                                        BLOCK_TONES[tone]
                                                    )}
                                                    style={{
                                                        top: top(start) + 1,
                                                        height: blockHeight,
                                                        left: `calc(${(lane / lanes) * 100}% + 2px)`,
                                                        width: `calc(${100 / lanes}% - 4px)`
                                                    }}
                                                    aria-label={`${patient?.name ?? 'Patient'}, ${formatTime(appointment.time)}, ${appointment.treatmentName}, ${APPOINTMENT_STATUS_META[appointment.status].label}`}
                                                />
                                            )}
                                        >
                                            <span className='block truncate font-semibold'>{patient?.name ?? 'Patient'}</span>
                                            {blockHeight > 34 && <span className='block truncate opacity-80'>{formatTime(appointment.time)} · {appointment.treatmentName}</span>}
                                        </AppointmentPopover>
                                    )
                                })}
                            </div>
                        )
                    })}
                </div>
            </div>
        </div>
    )
}

export default CalendarTimeGrid
